var _a;
import { createInstance } from '@optimizely/optimizely-sdk';
import { InstanceType, } from './optimizely.types';
import { OPTIMIZELY_COOKIE_NAME } from './optimizely.consts';
export class Optimizely {
    constructor(optimizelyInstance) {
        this.close = () => {
            return this.optimizelyInstance.close();
        };
        this.getSerializableDecisions = (decisions) => {
            const transformedDecisions = {};
            Object.keys(decisions).forEach((decisionKey) => {
                /* eslint-disable @typescript-eslint/naming-convention */
                const { userContext: _, ...rest } = decisions[decisionKey];
                transformedDecisions[decisionKey] = rest;
            });
            return transformedDecisions;
        };
        this.optimizelyInstance = optimizelyInstance;
    }
    static async getInstance({ sdkKey, dataFile, eventBatchSize = 5, additionalConfig, }) {
        if (!sdkKey && !dataFile) {
            return Promise.reject(new Error('Either sdkKey or dataFile must be provided'));
        }
        if (sdkKey && dataFile) {
            return Promise.reject(new Error('Please ensure only sdkKey or dataFile is provided'));
        }
        const instanceArguments = () => {
            if (sdkKey) {
                return {
                    instanceType: InstanceType.SDK_KEY,
                    value: sdkKey,
                };
            }
            return {
                instanceType: InstanceType.DATA_FILE,
                value: dataFile ?? '',
            };
        };
        const optimizelyInstance = await _a.createInstance({
            ...instanceArguments(),
            eventBatchSize,
            ...(additionalConfig && { additionalConfig }),
        });
        if (optimizelyInstance) {
            const instance = await optimizelyInstance
                .onReady()
                .then(({ success, reason }) => {
                if (success) {
                    return new _a(optimizelyInstance);
                }
                throw new Error(`Optimizely client initialization unsuccessful, reason: ${reason}`);
            })
                .catch((error) => {
                return Promise.reject(new Error(error));
            });
            return instance;
        }
        return Promise.reject(new Error(`Optimizely client initialisation unsuccessful, no instance available.`));
    }
    async getDataFile() {
        const dataFile = this.optimizelyInstance
            .getOptimizelyConfig()
            ?.getDatafile();
        return dataFile
            ? Promise.resolve(dataFile)
            : Promise.reject(new Error('Optimizely Datafile could not be found'));
    }
    async getDecisions({ experimentFlags, options, userId, userAttributes, forceDecisions = [], }) {
        if (!userId) {
            return Promise.reject(new Error('Failed to create Optimizely User Context in getDecisions. userId not found.'));
        }
        const user = this.optimizelyInstance.createUserContext(userId, userAttributes);
        if (!user) {
            return Promise.reject(new Error('Failed to create Optimizely User Context in getDecisions'));
        }
        forceDecisions.forEach(({ flagKey, ruleKey, variationKey }) => {
            user.setForcedDecision({
                flagKey,
                ...(ruleKey && { ruleKey }),
            }, { variationKey });
        });
        const decisions = user.decideForKeys(experimentFlags, options);
        const serializableDecisions = this.getSerializableDecisions(decisions);
        return serializableDecisions;
    }
    async trackEvent({ eventKey, userId, userAttributes, eventTags = {}, }) {
        if (!userId) {
            return Promise.reject(new Error('Failed to create Optimizely User Context in trackEvent. userId not found.'));
        }
        const user = this.optimizelyInstance.createUserContext(userId, userAttributes);
        if (!user) {
            return Promise.reject(new Error('Failed to create Optimizely User Context in trackEvent'));
        }
        const { revenue = null, value = null } = eventTags;
        const customEventProperties = {};
        Object.keys(eventTags).forEach((key) => {
            if (['revenue', 'value'].includes(key) === false) {
                customEventProperties[key] = eventTags[key] ?? null;
            }
        });
        const formattedEventTags = {
            $opt_event_properties: customEventProperties,
            ...(revenue ? { revenue } : {}),
            ...(value ? { value } : {}),
        };
        // type casted as the EventTags type exposed by the Optimizely SDK v4.X.X does not define custom properties
        user.trackEvent(eventKey, formattedEventTags);
        return Promise.resolve();
    }
}
_a = Optimizely;
Optimizely.createInstance = async ({ instanceType, value, eventBatchSize, additionalConfig, }) => {
    const initializeOptions = instanceType === InstanceType.SDK_KEY
        ? { sdkKey: value }
        : { datafile: value };
    return createInstance({
        ...initializeOptions,
        eventBatchSize,
        ...(additionalConfig && { ...additionalConfig }),
    });
};
export const getDecision = (decisions, experimentKey) => {
    if (experimentKey in decisions) {
        return decisions[experimentKey];
    }
    return undefined;
};
export const isExperimentEnabled = (decisions, experimentKey) => getDecision(decisions, experimentKey)?.enabled ?? false;
export const getExperimentVariationKey = (decisions, experimentKey) => getDecision(decisions, experimentKey)?.variationKey ?? undefined;
export const getExperimentVariables = (decisions, experimentKey) => getDecision(decisions, experimentKey)?.variables ?? undefined;
export const getCookieNameAndValue = (str, cookieName) => {
    const array = str.split(';');
    const path = array.find((attr) => attr.match('Path'))?.split('=')[1];
    // if the cookie is not set on the root path, we don't want to use it
    // we don't want duplicate cookies with different paths
    if (path !== '/') {
        return undefined;
    }
    const nameAndValueString = array.find((attr) => attr.match(cookieName));
    return nameAndValueString;
};
export const extractCookieValue = (setCookieHeader, cookieName) => {
    let cookieValue;
    if (Array.isArray(setCookieHeader)) {
        const values = setCookieHeader.map((cookie) => {
            return getCookieNameAndValue(cookie, cookieName);
        });
        cookieValue = values.find((v) => v?.match(cookieName));
    }
    else {
        // it's a string
        cookieValue = getCookieNameAndValue(setCookieHeader, cookieName);
    }
    return cookieValue?.split('=')[1];
};
export const getOptimizelyUserId = ({ cookies, response, cookieName = OPTIMIZELY_COOKIE_NAME, }) => {
    const getCookieFromResponse = () => {
        const setCookieHeader = response.getHeader('set-cookie');
        if (!setCookieHeader || typeof setCookieHeader === 'number') {
            return null;
        }
        return extractCookieValue(setCookieHeader, cookieName) ?? null;
    };
    return cookies[cookieName] || getCookieFromResponse();
};
//# sourceMappingURL=optimizely.js.map